import React, { useState, useEffect, useMemo, useCallback } from 'react'
import styled from '@emotion/styled'
import { LatLngTuple, LatLngExpression } from 'leaflet'
import L from 'leaflet'
import { FaFileDownload, FaFileUpload } from 'react-icons/fa'
import { CSVLink } from 'react-csv'
import CSVReader from 'react-csv-reader'

import { colors } from '../../../../../constants'
import { FlexContainer, YSpacing, DividerLine } from '@components/common'
import { Button } from '@components/common/form'
import {
  GOScheduleMenu,
  DateMenuSuggestion,
  DateMenuTemplate,
  GOClientLocation,
  Chef,
  ShowInfoModalReq,
  Option,
  Account,
  GOClientSettings,
} from '@types'
import {
  calcMenuTemplateOptions,
  getMenuCoordinates,
  generateMenuTemplateSuggestions,
  dateMenuSuggestionToCSV,
  csvToDateMenuSuggestions,
} from '../menuSuggestionsHelpers'
import DateMenuTemplatesManager from '../DateMenuTemplates/DateMenuTemplatesManager'
import ClientLocationSelector, {
  SearchClientSettingsParams,
} from '../ClientLocationSelector'
import {
  CreateDateMenuTemplateReq,
  UpdateDateMenuTemplateReq,
  SearchDateMenuTemplatesReq,
} from '@coordinators/groupOrders/dateMenuTemplates'

// Setup Leaflet custom icon
const greenChefIcon = new L.Icon({
  iconUrl: 'https://img.icons8.com/?size=100&id=20441&format=png&color=4CAF50',
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -30],
})

// Interface for map polylines
interface MapPolyline {
  id: string
  positions: LatLngExpression[]
  pathOptions: { color: string; weight: number; opacity?: number }
}

// Interface for map markers
interface MapMarker {
  id: string
  position: LatLngTuple
  icon: L.Icon
  popupContent: JSX.Element
}

// Parameters for the useDateMenusMap hook
interface UseDateMenusMapParams {
  hqId: number
  menus: GOScheduleMenu[]
  clientLocation?: GOClientLocation
  displayFailureMessage: (message: string) => void
  displayInfoMessage: (message: string) => void
}

// Return value from the useDateMenusMap hook
interface UseDateMenusMapResult {
  selectedDateMenuTemplate: DateMenuTemplate | null
  setSelectedDateMenuTemplate: (template: DateMenuTemplate) => void
  onGenerateDateMenuSuggestions: () => void
  deadendTemplateIndex: number | null
  dateMenuSuggestions: DateMenuSuggestion[]
  selectedSuggestion: DateMenuSuggestion | null
  handleSelectSuggestion: (suggestion: DateMenuSuggestion) => void
  handleClearSuggestions: () => void
  handleImportCSV: (data: any[], _fileInfo: any) => void
  renderUnsuggestedChefMenuGroups: boolean
  setRenderUnsuggestedChefMenuGroups: (render: boolean) => void
  renderAllDateMenuPolylines: boolean
  setRenderAllDateMenuPolylines: (render: boolean) => void
  suggestedMenus: { [key: string]: boolean }
  mapPolylines: MapPolyline[]
  mapCenter: LatLngTuple | null
  suggestionsMarkers: MapMarker[]
}

/**
 * Custom hook that manages date menu suggestions and related map visualizations
 */
export const useDateMenusMap = ({
  hqId,
  menus,
  clientLocation,
  displayFailureMessage,
  displayInfoMessage,
}: UseDateMenusMapParams): UseDateMenusMapResult => {
  const [selectedDateMenuTemplate, setSelectedDateMenuTemplate] =
    useState<DateMenuTemplate | null>(null)
  const [suggestedMenus, setSuggestedMenus] = useState<{
    [key: string]: boolean
  }>({})
  const [dateMenuSuggestions, setDateMenuSuggestions] = useState<
    DateMenuSuggestion[]
  >([])
  const [deadendTemplateIndex, setDeadendTemplateIndex] = useState<
    number | null
  >(null)
  const [selectedSuggestion, setSelectedSuggestion] =
    useState<DateMenuSuggestion | null>(null)
  const [renderUnsuggestedChefMenuGroups, setRenderUnsuggestedChefMenuGroups] =
    useState<boolean>(false)
  const [mapPolylines, setMapPolylines] = useState<MapPolyline[]>([])
  const [renderAllDateMenuPolylines, setRenderAllDateMenuPolylines] =
    useState<boolean>(false)
  const [mapCenter, setMapCenter] = useState<LatLngTuple | null>(null)

  console.log('datemenus mapCenter', mapCenter)

  // Update polyline when a suggestion is selected
  useEffect(() => {
    // Clear existing polylines if no suggestion is selected
    if (!selectedSuggestion) {
      if (!renderAllDateMenuPolylines) {
        setMapPolylines([])
      }

      return
    }

    // Get polylines for the selected suggestion
    const positions: LatLngExpression[] = []

    // Add coordinates for each menu in the route order
    selectedSuggestion.route.forEach((routeIndex) => {
      const menu = selectedSuggestion.menus[routeIndex]

      if (menu) {
        const coords = getMenuCoordinates(menu, hqId)
        positions.push([coords.latitude, coords.longitude])
      } else if (clientLocation) {
        // If no menu then its the client coordinate
        positions.push([
          clientLocation.coordinates.latitude,
          clientLocation.coordinates.longitude,
        ])
      }
    })

    // Create the selected suggestion polyline
    const selectedPolyline: MapPolyline = {
      id: `selected-suggestion-${selectedSuggestion.id}`,
      positions,
      pathOptions: {
        color: 'blue',
        weight: 4,
        opacity: 1.0,
      },
    }

    setMapPolylines([selectedPolyline])
  }, [selectedSuggestion, clientLocation, hqId, renderAllDateMenuPolylines])

  const onBuildAllDateMenuPolylines = useCallback(() => {
    const newMapPolylines: MapPolyline[] = []

    dateMenuSuggestions.forEach((suggestion, index) => {
      const positions: LatLngExpression[] = []
      suggestion.route.forEach((routeIndex) => {
        const menu = suggestion.menus[routeIndex]
        if (menu) {
          const coords = getMenuCoordinates(menu, hqId)
          positions.push([coords.latitude, coords.longitude])
        } else if (clientLocation) {
          positions.push([
            clientLocation.coordinates.latitude,
            clientLocation.coordinates.longitude,
          ])
        }
      })

      // Add this suggestion's polyline to the list
      newMapPolylines.push({
        id: `suggestion-route-${suggestion.id}-${index}`,
        positions,
        pathOptions: {
          color: 'blue',
          weight: 3,
          opacity: 0.7,
        },
      })
    })

    setMapPolylines(newMapPolylines)
  }, [dateMenuSuggestions, clientLocation, hqId])

  useEffect(() => {
    if (renderAllDateMenuPolylines) {
      onBuildAllDateMenuPolylines()
    } else {
      setMapPolylines([])
    }
  }, [renderAllDateMenuPolylines, onBuildAllDateMenuPolylines])

  // Clear all suggestions and reset selection state
  const handleClearSuggestions = useCallback(() => {
    setDateMenuSuggestions([])
    setSuggestedMenus({})
    setSelectedSuggestion(null)
    setMapPolylines([])
  }, [setDateMenuSuggestions])

  useEffect(() => {
    if (clientLocation) {
      handleClearSuggestions()
      setMapCenter([
        clientLocation.coordinates.latitude,
        clientLocation.coordinates.longitude,
      ])
    }
  }, [clientLocation, handleClearSuggestions])

  const onGenerateDateMenuSuggestions = useCallback(() => {
    if (!selectedDateMenuTemplate) {
      displayFailureMessage(
        'Please select a date menu template before generating suggestions',
      )

      return
    }
    if (!clientLocation) {
      displayFailureMessage(
        'Please select a client location before generating suggestions',
      )

      return
    }

    // Calculate all possilbe menus for each menu template
    const dateMenuOptions = selectedDateMenuTemplate.templates.map(
      (menuTemplate) => calcMenuTemplateOptions(menuTemplate, menus),
    )

    // Build all possible date menu combinations
    const suggestionsResult = generateMenuTemplateSuggestions(
      dateMenuOptions,
      [clientLocation],
      hqId,
    )

    const allPossibleSuggestions = suggestionsResult.suggestions
    if (suggestionsResult.deadendTemplateIndex !== undefined) {
      setDeadendTemplateIndex(suggestionsResult.deadendTemplateIndex)
    }

    setMapCenter([
      clientLocation.coordinates.latitude,
      clientLocation.coordinates.longitude,
    ])
    setDateMenuSuggestions(allPossibleSuggestions)
    setSuggestedMenus(
      allPossibleSuggestions
        .flatMap((suggestion) => suggestion.menus)
        .reduce(
          (acc, menu) => {
            acc[menu.id] = true

            return acc
          },
          {} as { [key: string]: boolean },
        ),
    )
  }, [
    selectedDateMenuTemplate,
    menus,
    clientLocation,
    hqId,
    displayFailureMessage,
    setDateMenuSuggestions,
  ])

  // Handle suggestion selection
  const handleSelectSuggestion = useCallback(
    (suggestion: DateMenuSuggestion) => {
      if (suggestion.id === selectedSuggestion?.id) {
        setSelectedSuggestion(null)
      } else {
        setSelectedSuggestion(suggestion)
        const firstMenu = suggestion.menus[0]
        if (firstMenu) {
          const coords = getMenuCoordinates(firstMenu, hqId)
          setMapCenter([coords.latitude, coords.longitude])
        }
      }
    },
    [selectedSuggestion, hqId],
  )

  const handleImportCSV = useCallback(
    (data: any[], _fileInfo: any) => {
      if (!clientLocation) {
        displayFailureMessage('No client location selected')

        return
      }

      const suggestionsImporter = csvToDateMenuSuggestions(
        displayFailureMessage,
        [clientLocation],
        menus,
        hqId,
      )
      const importedSuggestions = suggestionsImporter(data, _fileInfo)

      if (!importedSuggestions) {
        return
      }

      setDateMenuSuggestions(importedSuggestions)

      // Mark menus as suggested
      const newSuggestedMenus = importedSuggestions
        .flatMap((suggestion) => suggestion.menus)
        .reduce(
          (acc, menu) => {
            acc[menu.id] = true

            return acc
          },
          {} as { [key: string]: boolean },
        )

      setSuggestedMenus(newSuggestedMenus)

      displayInfoMessage(
        `Successfully imported ${importedSuggestions.length} date menu suggestions`,
      )
    },
    [
      clientLocation,
      displayFailureMessage,
      displayInfoMessage,
      menus,
      hqId,
      setDateMenuSuggestions,
    ],
  )

  // Create markers for suggestions
  const suggestionsMarkers = useMemo(() => {
    const filteredSuggestions = dateMenuSuggestions.filter((suggestion) => {
      if (renderAllDateMenuPolylines) {
        return true
      }

      if (selectedSuggestion) {
        return suggestion.id === selectedSuggestion.id
      }

      return false
    })

    return filteredSuggestions
      .flatMap((suggestion) => suggestion.menus)
      .map((menu, index) => {
        const coords = getMenuCoordinates(menu, hqId)

        return {
          id: `suggestion-menu-${menu.id}-${index}`,
          position: [coords.latitude, coords.longitude] as LatLngTuple,
          icon: greenChefIcon,
          popupContent: (
            <>
              <strong>{menu.chef.name}</strong>
              <br />
              {menu.name}
              <br />
              {menu.cuisineTags && menu.cuisineTags.join(', ')}
            </>
          ),
        }
      })
  }, [
    dateMenuSuggestions,
    selectedSuggestion,
    renderAllDateMenuPolylines,
    hqId,
  ])

  return {
    selectedDateMenuTemplate,
    setSelectedDateMenuTemplate,
    onGenerateDateMenuSuggestions,
    deadendTemplateIndex,
    dateMenuSuggestions,
    selectedSuggestion,
    handleSelectSuggestion,
    handleClearSuggestions,
    handleImportCSV,
    renderUnsuggestedChefMenuGroups,
    setRenderUnsuggestedChefMenuGroups,
    renderAllDateMenuPolylines,
    setRenderAllDateMenuPolylines,
    suggestedMenus,
    mapPolylines,
    mapCenter,
    suggestionsMarkers,
  }
}

// Props for the SuggestionList component
interface SuggestionListProps {
  // Date Menu Templates Manager props
  hqId: number
  userId: string
  userEmail: string
  loadChefs: (params: any) => Promise<Chef[]>
  displayFailureMessage: (message: string) => void
  displayInfoMessage: (message: string) => void
  closeInfoModal: () => void
  displayConfirmationModal: ({
    title,
    text,
  }: {
    title: string
    text: string
  }) => Promise<boolean>
  createDateMenuTemplate: (
    req: CreateDateMenuTemplateReq,
  ) => Promise<DateMenuTemplate | null>
  updateDateMenuTemplate: (
    req: UpdateDateMenuTemplateReq,
  ) => Promise<DateMenuTemplate | null>
  searchDateMenuTemplates: (
    req: SearchDateMenuTemplatesReq,
  ) => Promise<DateMenuTemplate[] | null>
  deleteDateMenuTemplates: (id: string) => Promise<boolean>
  menusCount: number
  showInfoModal: (req: ShowInfoModalReq) => void
  cuisineOptions: Option[]

  // Client selection props
  client: Account | null
  clientSettings: GOClientSettings | null
  withinBudget: boolean | null
  onChangeWithinBudget: (withinBudget: boolean) => void
  loadAccounts: (val: { search: string }) => Promise<Account[]>
  searchClientSettings: (
    params: SearchClientSettingsParams,
  ) => Promise<GOClientSettings[]>
  onSelectClientLocation: (
    account: Account,
    setting: GOClientSettings,
    location: GOClientLocation,
  ) => void

  // Template selection props
  selectedDateMenuTemplate: DateMenuTemplate | null
  setSelectedDateMenuTemplate: (template: DateMenuTemplate) => void
  onGenerateDateMenuSuggestions: () => void

  // Suggestion list props
  dateMenuSuggestions: DateMenuSuggestion[]
  deadendTemplateIndex: number | null
  numUnsuggestedChefs: number
  renderUnsuggestedChefMenuGroups: boolean
  renderAllDateMenuPolylines: boolean
  selectedSuggestion: DateMenuSuggestion | null
  handleSelectSuggestion: (suggestion: DateMenuSuggestion) => void
  handleClearSuggestions: () => void
  handleImportCSV: (data: any[], _fileInfo: any) => void
  setRenderUnsuggestedChefMenuGroups: (render: boolean) => void
  setRenderAllDateMenuPolylines: (render: boolean) => void
}

// SuggestionList component
export const SuggestionList: React.FC<SuggestionListProps> = ({
  hqId,
  userId,
  userEmail,
  loadChefs,
  displayFailureMessage,
  displayInfoMessage,
  closeInfoModal,
  displayConfirmationModal,
  createDateMenuTemplate,
  updateDateMenuTemplate,
  searchDateMenuTemplates,
  deleteDateMenuTemplates,
  showInfoModal,
  cuisineOptions,
  client,
  clientSettings,
  withinBudget,
  onChangeWithinBudget,
  loadAccounts,
  searchClientSettings,
  onSelectClientLocation,
  menusCount,
  selectedDateMenuTemplate,
  setSelectedDateMenuTemplate,
  onGenerateDateMenuSuggestions,
  dateMenuSuggestions,
  deadendTemplateIndex,
  numUnsuggestedChefs,
  selectedSuggestion,
  renderUnsuggestedChefMenuGroups,
  renderAllDateMenuPolylines,
  handleSelectSuggestion,
  handleClearSuggestions,
  handleImportCSV,
  setRenderUnsuggestedChefMenuGroups,
  setRenderAllDateMenuPolylines,
}) => {
  // Prepare CSV data
  const csvData = dateMenuSuggestionToCSV(dateMenuSuggestions)
  const csvFilename = `client-date-menu-suggestions-${
    new Date().toISOString().split('T')[0]
  }.csv`

  const handleSelectTemplate = (template: DateMenuTemplate) => {
    setSelectedDateMenuTemplate(template)
    closeInfoModal()
  }

  const showTemplatesManager = () => {
    showInfoModal({
      title: `Templates Manager`,
      content: (
        <DateMenuTemplatesManager
          hqId={hqId}
          userId={userId}
          userEmail={userEmail}
          cuisineOptions={cuisineOptions}
          loadChefs={loadChefs}
          displayConfirmationModal={displayConfirmationModal}
          displayInfoMessage={displayInfoMessage}
          displayFailureMessage={displayFailureMessage}
          createDateMenuTemplate={createDateMenuTemplate}
          updateDateMenuTemplate={updateDateMenuTemplate}
          searchDateMenuTemplates={searchDateMenuTemplates}
          deleteDateMenuTemplates={deleteDateMenuTemplates}
          onSelectTemplate={handleSelectTemplate}
        />
      ),
      width: '1650px',
    })
  }

  if (menusCount === 0) {
    return (
      <>
        <Note>Find menus before generating suggestions</Note>
        <YSpacing height="10px" />
      </>
    )
  }

  // If no suggestions are present, show template selection
  if (dateMenuSuggestions.length === 0) {
    return (
      <>
        <ClientLocationSelector
          initialClientSetting={clientSettings}
          onSelect={onSelectClientLocation}
          loadAccounts={loadAccounts}
          searchClientSettings={searchClientSettings}
          initialAccount={client}
          withinBudget={withinBudget}
          onChangeWithinBudget={onChangeWithinBudget}
        />
        <h3>Select a Template</h3>
        <YSpacing height="10px" />
        <FlexContainer flexDirection="column" width="100%">
          <Button
            width="100%"
            label={
              selectedDateMenuTemplate
                ? 'Select New Template'
                : 'Select Template'
            }
            onClick={showTemplatesManager}
          />
          {selectedDateMenuTemplate && (
            <>
              <YSpacing height="10px" />
              <InputLabel>Selected template:</InputLabel>
              <SelectedTemplateTitle>
                {selectedDateMenuTemplate.name} (
                {selectedDateMenuTemplate.templates.length} menus)
              </SelectedTemplateTitle>
            </>
          )}
        </FlexContainer>

        <YSpacing height="20px" />
        <GenerateButton
          onClick={onGenerateDateMenuSuggestions}
          disabled={!selectedDateMenuTemplate}
        >
          Generate Suggestions
        </GenerateButton>

        <YSpacing height="20px" />
        <DividerLine />
        <YSpacing height="20px" />

        <h3>Or Import Suggestions from CSV</h3>
        <YSpacing height="10px" />
        {/* CSV Import Button */}
        <ImportContainer>
          <FaFileUpload />
          <CSVReader
            cssClass="csv-import-input"
            cssLabelClass="csv-import-label"
            label="Import from CSV"
            onFileLoaded={handleImportCSV}
            parserOptions={{
              header: true,
              skipEmptyLines: true,
              transformHeader: (header: string) => header.trim(),
            }}
            inputStyle={{ display: 'none' }}
          />
        </ImportContainer>
        <YSpacing height="10px" />
        <p>* Import via CSV to edit date menus</p>
      </>
    )
  }

  // Otherwise, show suggestions list
  return (
    <>
      <FlexContainer
        flexDirection="column"
        justifyContent="space-between"
        alignItems="center"
      >
        <ClearButton onClick={handleClearSuggestions}>
          Clear Suggestions
        </ClearButton>

        <YSpacing height="20px" />

        {/* CSV Export Button */}
        {dateMenuSuggestions.length > 0 && (
          <CSVLink
            data={csvData}
            filename={csvFilename}
            style={{
              textDecoration: 'none',
              width: '100%',
              justifyItems: 'center',
            }}
            target="_blank"
          >
            <ExportButton>
              <FaFileDownload />
              Export to CSV
            </ExportButton>
          </CSVLink>
        )}

        <YSpacing height="10px" />
        {/* CSV Import Button */}
        <ImportContainer>
          <FaFileUpload />
          <CSVReader
            cssClass="csv-import-input"
            cssLabelClass="csv-import-label"
            label="Import Date Menus from CSV"
            onFileLoaded={handleImportCSV}
            parserOptions={{
              header: true,
              skipEmptyLines: true,
              transformHeader: (header: string) => header.trim(),
            }}
            inputStyle={{ display: 'none' }}
          />
        </ImportContainer>
        <YSpacing height="10px" />
        <p>* Import via CSV to edit date menus</p>
      </FlexContainer>
      <YSpacing height="20px" />

      <CheckboxWrapper>
        <FlexContainer flexDirection="column" alignItems="center" width="100%">
          <FlexContainer flexDirection="row">
            <CheckboxInput
              id="renderUnsuggestedChefMenuGroups"
              type="checkbox"
              checked={renderUnsuggestedChefMenuGroups}
              onChange={() =>
                setRenderUnsuggestedChefMenuGroups(
                  !renderUnsuggestedChefMenuGroups,
                )
              }
            />
            <CheckboxLabel htmlFor="renderUnsuggestedChefMenuGroups">
              Show {numUnsuggestedChefs} unsuggested chefs on map
            </CheckboxLabel>
          </FlexContainer>

          {dateMenuSuggestions.length > 0 && (
            <>
              <YSpacing height="20px" />
              <FlexContainer flexDirection="row">
                <CheckboxInput
                  id="renderAllDateMenuPolylines"
                  type="checkbox"
                  checked={renderAllDateMenuPolylines}
                  onChange={() =>
                    setRenderAllDateMenuPolylines(!renderAllDateMenuPolylines)
                  }
                />
                <CheckboxLabel htmlFor="renderUnsuggestedChefMenuGroups">
                  Show all {dateMenuSuggestions.length} date menu routes on map
                </CheckboxLabel>
              </FlexContainer>
            </>
          )}
        </FlexContainer>
      </CheckboxWrapper>

      {deadendTemplateIndex !== null && (
        <SidebarNote>
          <strong>Note:</strong> Suggestions reached a deadend on template{' '}
          {deadendTemplateIndex + 1}. Please add more menus that fit this
          template&apos;s requirements to allow for more variety of menus for
          the client.
        </SidebarNote>
      )}

      {dateMenuSuggestions.length > 0 && (
        <>
          <strong> {dateMenuSuggestions.length} date menus suggested </strong>
          <YSpacing height="10px" />
        </>
      )}

      <p>Select a suggestion to view the route:</p>
      <YSpacing height="10px" />

      {dateMenuSuggestions.map((suggestion, index) => (
        <SuggestionCard
          key={index}
          onClick={() => handleSelectSuggestion(suggestion)}
          selected={selectedSuggestion === suggestion}
        >
          <SuggestionTitle>Suggestion {index + 1}</SuggestionTitle>
          <SuggestionDetail>
            <strong>Total Distance:</strong>{' '}
            {suggestion.totalDistance.toFixed(2)} miles
          </SuggestionDetail>
          <SuggestionDetail>
            <strong>Menus:</strong> {suggestion.menus.length}
          </SuggestionDetail>
          <MenuListContainer>
            {suggestion.menus.map((menu, menuIndex) => (
              <React.Fragment key={menuIndex}>
                <MenuItemContainer>
                  <MenuItemName>{menu.name}</MenuItemName>
                  <MenuItemChef>by {menu.chef.name}</MenuItemChef>
                  <MenuItemCuisines>
                    {menu.cuisineTags &&
                      menu.cuisineTags.map((cuisine, cuisineIndex) => (
                        <CuisineTag key={cuisineIndex}>{cuisine}</CuisineTag>
                      ))}
                  </MenuItemCuisines>
                </MenuItemContainer>
                {menuIndex < suggestion.menus.length - 1 && <MenuSeparator />}
              </React.Fragment>
            ))}
          </MenuListContainer>
        </SuggestionCard>
      ))}
    </>
  )
}

// Styled components for the SuggestionList component
interface SuggestionCardProps {
  selected: boolean
}

const Note = styled.p`
  text-align: center;
`

// Base Card Style (to be used by Template, Chef, and Suggestion cards)
const BaseCard = styled.div`
  padding: 15px;
  margin-bottom: 12px;
  background-color: white;
  border: 1px solid ${colors.gray200};
  border-radius: 6px;
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;
`

const InteractiveCard = styled(BaseCard)<SuggestionCardProps>`
  cursor: pointer;
  background-color: ${(props) => (props.selected ? colors.blue100 : 'white')};
  border: 1px solid
    ${(props) => (props.selected ? colors.blue400 : colors.gray200)};
  box-shadow: ${(props) =>
    props.selected ? `0 2px 5px ${colors.blue100}` : 'none'};

  &:hover {
    border-color: ${colors.blue400};
    background-color: ${colors.blue100};
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  }
`

const SuggestionCard = styled(InteractiveCard)`` // Inherits all styles

const SuggestionTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 1.05em;
  font-weight: 600;
  color: ${colors.blue400};
`

const SuggestionDetail = styled.p`
  margin: 4px 0;
  font-size: 0.9em;
  color: ${colors.gray400};
  line-height: 1.3;

  strong {
    color: ${colors.dark500};
    font-weight: 500;
  }
`

// Styles for stacked menu list
const MenuListContainer = styled.div`
  width: 100%;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid ${colors.gray200};
`

const MenuItemContainer = styled.div`
  margin: 10px 0;
  padding: 8px;
  background-color: ${colors.gray100};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const MenuItemName = styled.div`
  font-size: 0.95em;
  font-weight: 500;
  color: ${colors.dark500};
  margin-bottom: 3px;
`

const MenuItemChef = styled.div`
  font-size: 0.85em;
  color: ${colors.gray400};
  margin-bottom: 4px;
`

const MenuItemCuisines = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const MenuSeparator = styled.div`
  height: 1px;
  background-color: ${colors.gray100}; // Lighter separator
  margin: 10px 0; // Space around separator
`

const GenerateButton = styled.button`
  padding: 10px 20px;
  background-color: ${(props) =>
    props.disabled ? colors.gray200 : colors.blue400};
  color: ${(props) => (props.disabled ? colors.gray400 : 'white')};
  border: none;
  border-radius: 6px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  font-weight: 600;
  font-size: 0.95em;
  width: 100%;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${(props) =>
      props.disabled ? colors.gray200 : colors.blue300};
  }
`

const ClearButton = styled.button`
  padding: 10px 20px;
  background-color: ${colors.gray100};
  color: ${colors.gray400};
  border: 1px solid ${colors.gray200};
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.95em;
  width: 100%;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;

  &:hover {
    background-color: ${colors.gray200};
    border-color: ${colors.gray300};
  }
`

const InputLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 0.9em;
  font-weight: bold;
  color: ${colors.dark500};
`

const CheckboxLabel = styled.label`
  font-size: 0.95em;
  font-weight: 500;
  color: ${colors.dark500};
  cursor: pointer;
  margin-left: 8px;
`

const CheckboxInput = styled.input`
  margin: 0;
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: ${colors.blue400};
`

// Wrapper for the checkbox to apply margin correctly
const CheckboxWrapper = styled.div`
  margin-bottom: 15px;
`

// Tag for cuisines
const CuisineTag = styled.span`
  background-color: ${colors.blue100};
  border: 1px solid ${colors.blue200};
  color: ${colors.blue400};
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 0.7em;
`

const SidebarNote = styled.p`
  font-size: 0.9em;
  color: ${colors.gray400};
  line-height: 1.4;
  margin-bottom: 10px;
  margin-top: 10px;
`

const ExportButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${colors.blue};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.95em;
  transition: background-color 0.2s ease;
  width: 100%;
  text-align: center;

  svg {
    margin-right: 8px;
  }

  &:hover {
    background-color: ${colors.green300};
  }
`

const ImportContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;

  svg {
    position: absolute;
    left: 12px;
    color: white;
    z-index: 1;
  }

  .csv-import-input {
    width: 100%;
  }

  .csv-import-label {
    background-color: ${colors.blue};
    color: white;
    border: none;
    border-radius: 6px;
    padding: 8px 12px 8px 35px;
    cursor: pointer;
    font-weight: 500;
    font-size: 0.95em;
    display: flex;
    justify-content: center;
    width: 100%;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: ${colors.blue300};
    }
  }
`

const SelectedTemplateTitle = styled.p`
  font-size: 1.1em;
  color: ${colors.blue};
  font-style: italic;
  text-decoration: underline;
  padding-left: 5px;
`
